// Quality presets (settings.video.quality). The individual toggles in settings.video (shadows,
// bloom, ...) stay authoritative; a preset scales how expensive the enabled features are. Low also
// skips the whole post-processing composer (`post: false`): bloom, FXAA, vignette, grain and the
// colour grade are off there whatever the toggles say; the other presets always run it, because the
// per-map exposure, tone mapping and grade live in the composer.

import type { Settings } from "../settings/schema";

export type Quality = Settings["video"]["quality"];

export interface QualityPreset {
  /** Shadow map edge length (texels). */
  shadowMapSize: number;
  /** Frames between shadow map refreshes while dynamic casters (players, projectiles) exist. */
  shadowEvery: number;
  /** Penumbra width of the sun shadow in shadow-map texels (5 Vogel-disk taps, so keep it small). */
  shadowRadius: number;
  /** Anisotropic filtering cap for map surface textures. */
  anisotropy: number;
  /** Edge scale of the generated surface textures (0.5 = a quarter of the texture memory). */
  textureScale: number;
  /** Whether the post-processing composer runs at all. */
  post: boolean;
  /** Bloom mip chain depth and render resolution factor. */
  bloomLevels: number;
  bloomResolutionScale: number;
  /** Particle pool scale (1 = full). */
  particleScale: number;
}

export const QUALITY_PRESETS: Readonly<Record<Quality, QualityPreset>> = {
  low: { shadowMapSize: 1024, shadowEvery: 4, shadowRadius: 1, anisotropy: 2, textureScale: 0.5, post: false, bloomLevels: 3, bloomResolutionScale: 0.35, particleScale: 0.5 },
  medium: { shadowMapSize: 2048, shadowEvery: 3, shadowRadius: 1.5, anisotropy: 4, textureScale: 1, post: true, bloomLevels: 4, bloomResolutionScale: 0.5, particleScale: 0.75 },
  high: { shadowMapSize: 2048, shadowEvery: 2, shadowRadius: 2, anisotropy: 8, textureScale: 1, post: true, bloomLevels: 5, bloomResolutionScale: 0.5, particleScale: 1 },
  ultra: { shadowMapSize: 4096, shadowEvery: 1, shadowRadius: 3, anisotropy: 16, textureScale: 1, post: true, bloomLevels: 6, bloomResolutionScale: 0.75, particleScale: 1 },
};

export function qualityPreset(q: Quality): QualityPreset {
  return QUALITY_PRESETS[q] ?? QUALITY_PRESETS.high;
}
